import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithDiscord: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true;
    
    const initializeAuth = async () => {
      try {
        // Check for existing session with retry logic
        let retries = 0;
        let sessionFound = false;
        
        while (retries < 3 && !sessionFound && mounted) {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Error getting session:', error);
          }
          
          if (session) {
            sessionFound = true;
            if (mounted) {
              console.log('Session initialized:', session.user.email);
              setSession(session);
              setUser(session.user);
              setLoading(false);
            }
          } else if (retries < 2) {
            // Wait before retry (useful for mobile/slow connections)
            console.log(`No session found, retry ${retries + 1}/3`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
          retries++;
        }
        
        if (!sessionFound && mounted) {
          console.log('No session found after retries');
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth event:', event, 'User:', session?.user?.email);
      
      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Don't set loading to false here if we're in the callback process
        if (event !== 'SIGNED_IN' || window.location.pathname !== '/auth/callback') {
          setLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password })
    if (error) throw error
  }

  const signInWithGoogle = async () => {
    console.log('Starting Google sign in...');
    console.log('User agent:', navigator.userAgent);
    console.log('Is mobile:', /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    
    // Build the correct redirect URL with callback path
    const baseUrl = window.location.origin;
    const redirectUrl = `${baseUrl}/auth/callback`;
    
    console.log('Base URL:', baseUrl);
    console.log('Redirect URL:', redirectUrl);
    
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      })
      
      console.log('OAuth response:', { data, error });
      
      if (error) {
        console.error('OAuth error:', error);
        throw error;
      }
      
      // Log the auth URL for debugging
      if (data?.url) {
        console.log('Auth URL:', data.url);
      }
    } catch (err) {
      console.error('Sign in error:', err);
      throw err;
    }
  }

  const signInWithDiscord = async () => {
    console.log('Starting Discord sign in...');
    
    const baseUrl = window.location.origin;
    const redirectUrl = `${baseUrl}/auth/callback`;
    
    console.log('Redirect URL:', redirectUrl);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        redirectTo: redirectUrl,
        scopes: 'identify', // Get username from Discord
      }
    })
    
    console.log('Discord sign in response:', { data, error });
    
    if (error) {
      console.error('Discord sign in error:', error);
      throw error;
    }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signIn,
      signUp,
      signInWithGoogle,
      signInWithDiscord,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  )
}